/**
 * Shape helpers for the in-meeting chat, shared by the web and the mobile
 * views (no React, no DOM, so the mobile app can import them).
 */
import type { MeetChatMessage } from './meetApi';

export interface ChatSenderRun {
  key: string;
  userId: string | null;
  participantId: string;
  senderName: string;
  lines: MeetChatMessage[];
}

/** Fold consecutive lines by the same participant into one run, like a chat log. */
export function groupBySender(messages: MeetChatMessage[]): ChatSenderRun[] {
  const runs: ChatSenderRun[] = [];
  for (const m of messages) {
    const last = runs[runs.length - 1];
    if (last && last.participantId === m.participantId) {
      last.lines.push(m);
    } else {
      runs.push({
        key: m.id,
        userId: m.userId,
        participantId: m.participantId,
        senderName: m.senderName,
        lines: [m],
      });
    }
  }
  return runs;
}

export const chatTimeFormat = new Intl.DateTimeFormat('ro-RO', {
  hour: '2-digit',
  minute: '2-digit',
});

/** Plain text for the clipboard: "[HH:MM] Name: text", one line each. */
export function chatToText(messages: MeetChatMessage[]): string {
  return messages
    .map((m) => `[${chatTimeFormat.format(new Date(m.createdAt))}] ${m.senderName}: ${m.text}`)
    .join('\n');
}

/** Is this run ours? By participant row inside the room, by user afterwards. */
export function isOwnRun(
  run: Pick<ChatSenderRun, 'participantId' | 'userId'>,
  me: { participantId?: string; userId?: string },
): boolean {
  return (
    (Boolean(me.participantId) && run.participantId === me.participantId) ||
    (Boolean(me.userId) && run.userId === me.userId)
  );
}
